import * as A from 'astronomy-engine';
import type { Selection } from '../state';
import { addDays, wallTime } from '../lib/localtime';
import { type HijriMonth, type Starts, hijriAt, hijriName, isCrescentDay, isSoughtMonth, monthKey, monthStart, nextMonth, saudiDate, sunsetOn } from './hijri';
import { Shower, hourlyRate, radiantAt, solarLongitude, upcomingPeaks, zhrAt } from './meteors';
// Free of the page (no renderer, no saved state), so the events worker can run all of this.
import { limitingMagnitude } from './skybrightness';

export interface SkyEvent {
  t: number;
  kind: 'moon' | 'meteor' | 'eclipse' | 'conjunction' | 'opposition' | 'elongation' | 'season' | 'crescent' | 'day';
  title: string;
  detail: string;
  icon: string;
  select?: Selection;
  weight: number; // for highlighting: 3 major, 2 notable, 1 routine
  /** The same for the same event however often it is worked out: what it is and its UTC day. */
  id: string;
  /** Best seen in the hours of darkness, so dated as a night ("night of 21–22 Oct"). */
  night?: boolean;
  /** A meteor shower: when it is active at all. */
  active?: { from: number; to: number };
  /** Expected meteors an hour from here at the best time, under a clear sky (showers). */
  rate?: number;
  /** A meteor shower's rate under perfect skies at its peak (null when it varies from year to year). */
  zhr?: number | null;
  /** An eclipse: its stages, in time order, each with whether the Sun or Moon is up then. */
  phases?: { label: string; t: number; up: boolean }[];
  /** Whether it can be seen from here at all (eclipses). */
  visible?: boolean;
  /** An eclipse: which kind it is from here (a solar eclipse partial here may be total elsewhere). */
  type?: 'penumbral' | 'partial' | 'total' | 'annular';
  /** The evening of the 29th: the Hijri month whose crescent is looked for; a day of the Hijri year: its month. */
  hijri?: HijriMonth;
  /** A day of the Hijri year (DECISIONS item 79): ʿArafah or ʿĀshūrāʾ, and the sunset that ends it here. */
  day?: 'arafah' | 'ashura';
  until?: number;
  /** A Moon phase: 0 New Moon, 1 first quarter, 2 Full Moon, 3 last quarter. */
  moonQuarter?: 0 | 1 | 2 | 3;
  /** A Moon phase that is a "supermoon": a Full Moon closer than about 361,000 km. */
  supermoon?: boolean;
}

export interface EventOptions {
  /** Bortle class of the sky here, for meteor rates (default 5, a suburban sky). */
  bortle?: number;
  /** Saudi Arabia's decided month starts (src/live/saudi.ts), for the evenings of the 29th. */
  starts?: Starts;
  /** The place's time zone, for its own dates (else guessed from the longitude, which fails near the date line). */
  tz?: string;
}

const PLANETS = [A.Body.Mercury, A.Body.Venus, A.Body.Mars, A.Body.Jupiter, A.Body.Saturn];
const AU_KM = 149597870.7;
const DAY = 86400e3;
const utcDay = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Separation as seen from the observer (the Moon's parallax can be a whole degree). */
function sepFrom(a: A.Body, b: A.Body, t: Date, obs: A.Observer) {
  return A.AngleBetween(A.Equator(a, t, obs, true, true).vec, A.Equator(b, t, obs, true, true).vec);
}

function visibleAt(body: A.Body, t: Date, obs: A.Observer) {
  return horizontal(body, t, obs).altitude;
}

function horizontal(body: A.Body, t: Date, obs: A.Observer) {
  const e = A.Equator(body, t, obs, true, true);
  return A.Horizon(t, obs, e.ra, e.dec, 'normal');
}

const COMPASS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];
const compass = (az: number) => COMPASS[Math.round((((az % 360) + 360) % 360) / 45) % 8];
const cap = (s: string) => s[0].toUpperCase() + s.slice(1);

/**
 * The best moment near `t` to see two objects together from here: both at least 10° up, the Sun at
 * least 6° down, still close together; preferring height above the horizon and a darker sky. Checked every
 * 10 minutes: a low pairing in twilight can be in view for only a quarter of an hour (Venus and Saturn
 * from Makkah before dawn on 8 May 2027).
 */
function bestViewing(a: A.Body, b: A.Body, t: number, obs: A.Observer, maxSep: number, hours: number): { t: number; alt: number; az: number } | null {
  let best: { t: number; alt: number; az: number } | null = null, score = -Infinity;
  for (let dt = -hours * 3600e3; dt <= hours * 3600e3; dt += 10 * 60e3) {
    const d = new Date(t + dt);
    const sun = visibleAt(A.Body.Sun, d, obs);
    if (sun > -6) {
      if (sun > 10) dt += 60 * 60e3; // broad daylight: skip ahead
      continue;
    }
    const ha = horizontal(a, d, obs), hb = horizontal(b, d, obs);
    const low = Math.min(ha.altitude, hb.altitude);
    if (low < 10 || sepFrom(a, b, d, obs) > maxSep) continue;
    const sc = Math.min(low, 35) + (sun < -12 ? 3 : 0) - (Math.abs(dt) / 3600e3) * 0.15;
    if (sc > score) {
      score = sc;
      best = { t: d.getTime(), alt: low, az: hb.azimuth };
    }
  }
  return best;
}

/** The moment near `near` when the Sun reaches solar longitude `lon` (two Newton steps suffice). */
function whenSunAt(lon: number, near: number): number {
  let t = near;
  for (let k = 0; k < 3; k++) {
    let e = lon - solarLongitude(new Date(t));
    if (e > 180) e -= 360;
    if (e < -180) e += 360;
    t += (e / 0.9856) * DAY;
  }
  return t;
}
const after = (l: number, from: number) => (((l - from) % 360) + 360) % 360;

/**
 * How deep the Moon goes into the Earth's outer shadow (the penumbral magnitude), by the same geometry
 * Astronomy Engine uses to find lunar eclipses. Below about 0.7 the shading cannot be noticed by eye.
 */
function penumbralMagnitude(t: Date): number {
  const SUN_KM = 695700, EARTH_KM = 6371 + 88, MOON_KM = 1737.4; // the Earth with the atmosphere's share of the shadow
  const s = A.GeoVector(A.Body.Sun, t, true);
  const m = A.GeoMoon(t);
  const u = -(s.x * m.x + s.y * m.y + s.z * m.z) / (s.x * s.x + s.y * s.y + s.z * s.z);
  const fromAxis = A.KM_PER_AU * Math.hypot(u * -s.x - m.x, u * -s.y - m.y, u * -s.z - m.z);
  const penumbra = -SUN_KM + (1 + u) * (SUN_KM + EARTH_KM);
  return (penumbra - fromAxis + MOON_KM) / (2 * MOON_KM);
}

type Phase = { label: string; t: number; up: boolean };
/** An eclipse's stages with the Sun's or Moon's rising or setting added where it falls during them, so its times here run from when it can be seen. */
function withRiseSet(phases: Phase[], body: A.Body, obs: A.Observer): Phase[] {
  if (!phases.some((x) => x.up) || phases.every((x) => x.up)) return phases;
  const first = phases[0].t, last = phases[phases.length - 1].t;
  const name = body === A.Body.Sun ? 'Sun' : 'Moon';
  const out = [...phases];
  for (const dir of [+1, -1]) {
    const r = A.SearchRiseSet(body, obs, dir, new Date(first), (last - first) / DAY);
    if (r && r.date.getTime() < last) out.push({ label: `${name}${dir > 0 ? 'rise' : 'set'}`, t: r.date.getTime(), up: true });
  }
  return out.sort((a, b) => a.t - b.t);
}

/** When an event is over: an eclipse's last stage, a shower's last active day, a day of the Hijri year at the sunset
 *  that ends it (it is dated at midday), anything else at its time. */
export const eventEnd = (e: SkyEvent) => (e.phases?.length ? e.phases[e.phases.length - 1].t : e.active ? e.active.to : e.kind === 'day' ? (e.until ?? e.t + 6 * 3600e3) : e.t);

/** Everything notable in the sky over the next `days`, soonest first. */
export function upcomingEvents(obs: A.Observer, from: Date, days: number, showers: Shower[], opts: EventOptions = {}): SkyEvent[] {
  const end = from.getTime() + days * DAY;
  const out: SkyEvent[] = [];
  const inWin = (t: number) => t >= from.getTime() - 3600e3 && t <= end;
  const bortle = opts.bortle ?? 5;

  // Moon phases, flagging "supermoons" (full Moon within about 360,000 km).
  let q = A.SearchMoonQuarter(from);
  for (let i = 0; i < 60 && q.time.date.getTime() <= end; i++) {
    const t = q.time.date;
    const names = ['New Moon', 'First quarter', 'Full Moon', 'Last quarter'];
    let title = names[q.quarter];
    let detail = '';
    let weight = q.quarter === 2 ? 2 : 1;
    let supermoon = false;
    if (q.quarter === 2) {
      const km = A.GeoVector(A.Body.Moon, t, true).Length() * AU_KM;
      if (km < 361000) {
        title = 'Full Moon (a “supermoon”)';
        detail = `Closer than usual, ${Math.round(km).toLocaleString('en-GB')} km away, so slightly bigger and brighter.`;
        weight = 3;
        supermoon = true;
      }
    }
    if (q.quarter === 0) detail = 'The darkest skies of the month for faint stars and the Milky Way.';
    // No "New Moon" on the 29th, the 30th or the 1st here (where it usually falls): that evening is the crescent's.
    const day = q.quarter === 0 && opts.tz ? hijriAt(t.getTime(), { lat: obs.latitude, lon: obs.longitude, tz: opts.tz }, opts.starts ?? {}).day : 0;
    if (isCrescentDay(day)) {
      q = A.NextMoonQuarter(q);
      continue;
    }
    out.push({
      t: t.getTime(), kind: 'moon', title, detail, icon: 'moon', select: { kind: 'body', id: 'Moon' }, weight, id: `moon-${q.quarter}-${utcDay(t.getTime())}`,
      moonQuarter: q.quarter as 0 | 1 | 2 | 3, supermoon,
    });
    q = A.NextMoonQuarter(q);
  }

  // The new crescent (hilāl): the evening of each 29th by Saudi Arabia's calendar, when the crescent of the next month
  // is looked for, at sunset here. Nothing is said of whether it can be seen (BUILD-SPEC items 19 and 40): Saudi
  // Arabia's announcement decides the month.
  const starts = opts.starts ?? {};
  let month: HijriMonth = saudiDate(addDays(utcDay(from.getTime()), -32), starts);
  for (let i = 0; i < Math.ceil(days / 29) + 3; i++, month = nextMonth(month)) {
    const date = addDays(monthStart(month, starts), 28);
    // The sunset that ends that date here, when the Hijri date turns (src/astro/hijri.ts); without the time zone,
    // searched from about local midnight, the time zone guessed from the longitude.
    const sunset = opts.tz
      ? sunsetOn(date, { lat: obs.latitude, lon: obs.longitude, tz: opts.tz })
      : A.SearchRiseSet(A.Body.Sun, obs, -1, new Date(Date.parse(`${date}T00:00:00Z`) - (obs.longitude / 15) * 3600e3), 1)?.date.getTime();
    if (sunset == null || !inWin(sunset)) continue;
    const sought = nextMonth(month);
    out.push({
      t: sunset, kind: 'crescent', icon: 'moon', weight: isSoughtMonth(sought) ? 3 : 2,
      title: `New crescent (hilāl) of ${hijriName(sought)}`,
      detail: 'Look low in the west just after sunset.',
      // By the month looked for, so the id (and any reminder set for it) stays when Saudi Arabia moves a month.
      select: { kind: 'body', id: 'Moon' }, hijri: sought, id: `crescent-${monthKey(sought)}`,
    });
  }

  // ʿArafah (9 Dhū al-Ḥijjah) and ʿĀshūrāʾ (10 Muḥarram) by Saudi Arabia's calendar (DECISIONS item 79), dated by the day
  // itself, at midday here.
  const year0 = saudiDate(utcDay(from.getTime()), starts).year;
  for (let year = year0 - 1; year <= year0 + 2; year++) {
    for (const [month, n, day, title] of [[12, 9, 'arafah', 'The day of ʿArafah'], [1, 10, 'ashura', 'The day of ʿĀshūrāʾ']] as const) {
      const date = addDays(monthStart({ month, year }, starts), n - 1);
      const t = opts.tz ? wallTime(date, opts.tz, 12) : Date.parse(`${date}T12:00:00Z`) - (obs.longitude / 15) * 3600e3;
      if (!inWin(t)) continue;
      const until = opts.tz ? sunsetOn(date, { lat: obs.latitude, lon: obs.longitude, tz: opts.tz }) : t + 6 * 3600e3;
      out.push({ t, kind: 'day', icon: 'calendar', weight: 3, title, detail: '', id: `day-${day}-${year}`, hijri: { month, year }, day, until });
    }
  }

  // Meteor showers: the night of each published maximum (or the nearer one, when it falls by day), at the hour
  // then with the most meteors to see from here (the radiant high, the sky dark, moonlight taken into account),
  // and the dates the shower is active, in whole days as the IMO gives them.
  const day = (t: number) => Math.round(t / DAY) * DAY + DAY / 2; // the nearest UT date, held at its noon
  const count = (x: number) => (x < 0.5 ? 'fewer than one an hour' : x < 1.5 ? 'about one an hour' : `about ${Math.round(x)} an hour`);
  for (const p of upcomingPeaks(showers, from, days)) {
    const s = p.shower;
    if ((s.zhr ?? 0) < 5 && !s.variable) continue;
    const pk = p.date.getTime();
    let best: { t: number; rate: number; dark: number; alt: number; moonAlt: number; lit: number; score: number } | null = null;
    for (let dt = -30 * 3600e3; dt <= 30 * 3600e3; dt += 15 * 60e3) {
      const d = new Date(pk + dt);
      const sun = visibleAt(A.Body.Sun, d, obs);
      if (sun > -12) continue;
      const lam = solarLongitude(d);
      const rad = radiantAt(s, lam);
      const alt = A.Horizon(d, obs, rad.ra / 15, rad.dec, 'normal').altitude;
      if (alt <= 0) continue;
      const moon = horizontal(A.Body.Moon, d, obs);
      const ill = A.Illumination(A.Body.Moon, d);
      const rate = hourlyRate(zhrAt(s, lam), alt, s.r, limitingMagnitude(sun, moon.altitude, ill.mag, bortle));
      // Nearer the published maximum counts for more: between the dates the activity model is only a smooth
      // guess, and must not move the night away from the maximum (the Ursids peak on 22–23 December, not 21–22).
      const score = rate * Math.exp(-((dt / (12 * 3600e3)) ** 2));
      if (!best || score > best.score) {
        // The same moment under a dark rural sky (Bortle 2), to show what town lights take away.
        const dark = hourlyRate(zhrAt(s, lam), alt, s.r, limitingMagnitude(sun, moon.altitude, ill.mag, 2));
        best = { t: d.getTime(), rate, dark, alt, moonAlt: moon.altitude, lit: ill.phase_fraction, score };
      }
    }
    // A shower whose radiant never rises here in darkness cannot be seen.
    if (!best || !inWin(best.t)) continue;
    // For some showers the IMO date is only where the radiant is listed, not a maximum.
    const noPeak = /reference date/i.test(s.note);
    const darker = bortle > 3 && Math.round(best.dark) >= Math.max(3, Math.round(best.rate) * 2) ? ` Away from town lights, ${count(best.dark)}.` : '';
    const here = s.zhr == null ? 'From here, the radiant is best placed around then.' : `From here, ${count(best.rate)} at the best time if the sky is clear.${darker}`;
    const moonNote = best.moonAlt > 0 && best.lit > 0.25 ? ` The Moon (${Math.round(best.lit * 100)}% lit) is up then and hides the fainter ones.` : best.moonAlt <= 0 ? ' The Moon is down then.' : '';
    const low = best.alt > 10 ? '' : ' The radiant stays low from here.';
    const rateText = s.zhr ? `At ${noPeak ? 'best' : 'its peak'}, about ${s.zhr} an hour under perfect skies with the radiant overhead.` : 'Its rates vary a lot from year to year and are hard to predict.';
    out.push({
      t: best.t, kind: 'meteor', icon: 'meteor', weight: noPeak ? 1 : (s.zhr ?? 0) >= 50 && best.alt > 10 ? 3 : (s.zhr ?? 0) >= 15 ? 2 : 1,
      title: noPeak ? `${s.name} active` : `${s.name} peak`,
      detail: `${rateText} ${here}${moonNote}${low}`,
      select: { kind: 'shower', code: s.code }, night: true, rate: best.rate, zhr: s.zhr,
      // (The end is at least a little after the peak, as meteors.ts has it: one listed end falls just before.)
      active: { from: day(whenSunAt(s.lStart, pk - (after(s.lPeak, s.lStart) / 0.9856) * DAY)), to: day(whenSunAt(Math.max(s.lEnd, s.lPeak + 0.4), pk + (after(Math.max(s.lEnd, s.lPeak + 0.4), s.lPeak) / 0.9856) * DAY)) },
      id: `meteor-${s.code}-${new Date(pk).getUTCFullYear()}`,
    });
  }

  // Eclipses. Lunar: seen from wherever the Moon is up. Solar: only where the Moon's shadow falls.
  const minutes = (m: number) => m * 60e3;
  try {
    let le = A.SearchLunarEclipse(new Date(from.getTime() - DAY));
    for (let i = 0; i < 8 && le.peak.date.getTime() <= end; i++, le = A.NextLunarEclipse(le.peak)) {
      const tp = le.peak.date.getTime();
      if (!inWin(tp)) continue;
      const moonUp = (t: number) => visibleAt(A.Body.Moon, new Date(t), obs) > 0;
      const stage = (label: string, t: number) => ({ label, t, up: moonUp(t) });
      const phases =
        le.kind === 'penumbral'
          ? [stage('Shading begins', tp - minutes(le.sd_penum)), stage('Greatest', tp), stage('Shading ends', tp + minutes(le.sd_penum))]
          : [
              stage('Partial phase begins', tp - minutes(le.sd_partial)),
              ...(le.kind === 'total' ? [stage('Totality begins', tp - minutes(le.sd_total))] : []),
              stage('Greatest', tp),
              ...(le.kind === 'total' ? [stage('Totality ends', tp + minutes(le.sd_total))] : []),
              stage('Partial phase ends', tp + minutes(le.sd_partial)),
            ];
      const ups = phases.filter((x) => x.up).length;
      const visible = ups > 0;
      const slight = le.kind === 'penumbral' && penumbralMagnitude(le.peak.date) < 0.7;
      const look =
        le.kind === 'total' ? 'The whole Moon turns a coppery red.'
        : le.kind === 'partial' ? 'Part of the Moon turns dark.'
        : slight ? 'A very faint shading, unlikely to be noticed by eye.'
        : 'A faint shading over part of the Moon, easy to miss.';
      const where = !visible
        ? 'Not visible from here: the Moon is below the horizon.'
        : slight
          ? ''
          : ups === phases.length
            ? 'Visible from here from start to finish.'
            : phases[0].up
              ? 'Visible from here until the Moon sets.'
              : 'Visible from here once the Moon has risen.';
      out.push({
        t: tp, kind: 'eclipse', icon: 'moon', weight: !visible || slight ? 1 : le.kind === 'penumbral' ? 2 : 3,
        title: `${cap(le.kind)} lunar eclipse${visible ? '' : ', not seen here'}`, type: le.kind as 'penumbral' | 'partial' | 'total',
        detail: `${look} ${where}`.trim(), select: { kind: 'body', id: 'Moon' }, phases: withRiseSet(phases, A.Body.Moon, obs), visible, id: `eclipse-lunar-${utcDay(tp)}`,
      });
    }
  } catch { /* none */ }
  const solarSeen: number[] = [];
  try {
    let se = A.SearchLocalSolarEclipse(new Date(from.getTime() - DAY), obs);
    for (let i = 0; i < 8 && se.peak.time.date.getTime() <= end; i++, se = A.NextLocalSolarEclipse(se.peak.time, obs)) {
      const tp = se.peak.time.date.getTime();
      // From here the Sun may be below the horizon for the whole of it: then it is not an event here.
      const raw = [
        { label: 'Begins', e: se.partial_begin },
        ...(se.total_begin ? [{ label: se.kind === 'annular' ? 'Ring begins' : 'Totality begins', e: se.total_begin }] : []),
        { label: 'Greatest', e: se.peak },
        ...(se.total_end ? [{ label: se.kind === 'annular' ? 'Ring ends' : 'Totality ends', e: se.total_end }] : []),
        { label: 'Ends', e: se.partial_end },
      ];
      const phases = raw.map((x) => ({ label: x.label, t: x.e.time.date.getTime(), up: x.e.altitude > 0 }));
      if (!inWin(tp) || !phases.some((x) => x.up)) continue;
      solarSeen.push(tp);
      const pct = Math.round(se.obscuration * 100);
      const sunNote = phases.every((x) => x.up) ? '' : phases[0].up ? ' The Sun sets before it ends.' : ' The Sun rises with it already under way.';
      // How long totality (or the ring) lasts, to the half minute: models differ by a few seconds.
      const secs = se.total_begin && se.total_end ? (se.total_end.time.date.getTime() - se.total_begin.time.date.getTime()) / 1000 : 0;
      const halves = Math.round(secs / 30);
      const long = secs < 75 ? `about ${Math.max(5, Math.round(secs / 5) * 5)} seconds` : `about ${Math.floor(halves / 2)}${halves % 2 ? '½' : ''} minutes`;
      const what = se.kind === 'total' ? `Totality here: the Sun is completely covered for ${long}.` : se.kind === 'annular' ? `A ring of Sun around the Moon here, for ${long}.` : `About ${pct}% of the Sun covered from here.`;
      // NASA's advice: eclipse glasses at every moment except totality itself, and solar filters on any optics.
      const safety = se.kind === 'total'
        ? ' Wear eclipse glasses for the partial phases: only during totality is it safe to look without them.'
        : ' Never look at the Sun without eclipse glasses or a safe solar viewer.';
      out.push({
        t: tp, kind: 'eclipse', icon: 'sun', weight: 3, title: `${cap(se.kind)} solar eclipse`, type: se.kind as 'partial' | 'annular' | 'total',
        detail: `${what}${sunNote}${safety} Binoculars and telescopes need a proper solar filter.`,
        select: { kind: 'body', id: 'Sun' }, phases: withRiseSet(phases, A.Body.Sun, obs), visible: true, id: `eclipse-solar-${utcDay(tp)}`,
      });
    }
  } catch { /* none */ }
  // The great solar eclipses elsewhere on Earth, so they are not missed when travelling.
  try {
    let ge = A.SearchGlobalSolarEclipse(new Date(from.getTime() - DAY));
    for (let i = 0; i < 6 && ge.peak.date.getTime() <= end; i++, ge = A.NextGlobalSolarEclipse(ge.peak)) {
      const tp = ge.peak.date.getTime();
      if (!inWin(tp) || ge.kind === 'partial' || solarSeen.some((t) => Math.abs(t - tp) < DAY)) continue;
      out.push({
        t: tp, kind: 'eclipse', icon: 'sun', weight: 1, title: `${cap(ge.kind)} solar eclipse, not seen here`, type: ge.kind as 'total' | 'annular',
        detail: 'It can be seen only from elsewhere on Earth.', select: { kind: 'body', id: 'Sun' }, visible: false, id: `eclipse-solar-${utcDay(tp)}`,
      });
    }
  } catch { /* none */ }

  // Oppositions (best time to see the outer planets) and greatest elongations (Mercury, Venus).
  for (const b of [A.Body.Mars, A.Body.Jupiter, A.Body.Saturn, A.Body.Uranus, A.Body.Neptune]) {
    let t0 = from;
    for (let i = 0; i < 3; i++) {
      let opp: Date;
      try {
        opp = A.SearchRelativeLongitude(b, 0, t0).date;
      } catch {
        break;
      }
      if (opp.getTime() > end + DAY) break;
      // Show it when it is highest, around midnight, rather than at the exact (possibly daytime) moment.
      const t = A.SearchHourAngle(b, obs, 0, new Date(opp.getTime() - 12 * 3600e3)).time.date;
      if (inWin(t.getTime())) out.push({ t: t.getTime(), kind: 'opposition', icon: 'planet', weight: b === A.Body.Mars || b === A.Body.Jupiter || b === A.Body.Saturn ? 3 : 1, title: `${b} at opposition`, detail: `${b} is opposite the Sun: up all night and at its brightest this year.`, select: { kind: 'body', id: b }, night: true, id: `opp-${b}-${utcDay(opp.getTime())}` });
      t0 = new Date(opp.getTime() + 30 * DAY);
    }
  }
  for (const b of [A.Body.Mercury, A.Body.Venus]) {
    let t0 = from;
    for (let i = 0; i < 10; i++) {
      let e: A.ElongationEvent;
      try {
        e = A.SearchMaxElongation(b, t0);
      } catch {
        break;
      }
      if (e.time.date.getTime() > end + DAY) break;
      t0 = new Date(e.time.date.getTime() + 10 * DAY);
      if (!inWin(e.time.date.getTime())) continue;
      // How high is it from here when the sky gets dark enough (the Sun 6° down)?
      const evening = e.visibility === 'evening';
      const dayStart = new Date(e.time.date.getTime() - 12 * 3600e3);
      const tw = A.SearchAltitude(A.Body.Sun, obs, evening ? -1 : +1, dayStart, 1, -6)?.date;
      const h = tw ? horizontal(b, tw, obs) : null;
      const low = !h || h.altitude < 5;
      out.push({
        t: (tw ?? e.time.date).getTime(), kind: 'elongation', icon: 'planet', weight: low ? 1 : 2,
        title: `${b} near its greatest ${e.visibility} elongation`,
        detail: low
          ? `${Math.round(e.elongation)}° from the Sun, but from here it stays too low in the ${evening ? 'evening' : 'morning'} twilight to see easily.`
          : `${Math.round(e.elongation)}° from the Sun: about ${Math.round(h!.altitude)}° up in the ${compass(h!.azimuth)} as ${evening ? 'dusk falls' : 'dawn breaks'}.`,
        select: { kind: 'body', id: b }, id: `elong-${b}-${utcDay(e.time.date.getTime())}`,
      });
    }
  }

  // Close pairings: the Moon near bright planets, and planets near each other (sampled every 3 h).
  // Positions are computed once per sample and shared between all pairs.
  const bodies = [A.Body.Moon, ...PLANETS];
  // On a fixed grid of whole 3-hour UTC steps, so the same pairing gets the same time and id whenever the list starts.
  const times: number[] = [];
  for (let t = Math.ceil(from.getTime() / (3 * 3600e3)) * 3 * 3600e3; t <= end; t += 3 * 3600e3) times.push(t);
  const vecs = bodies.map((b) => times.map((t) => A.GeoVector(b, new Date(t), true)));
  const pairs: [number, number, number][] = [];
  for (let j = 1; j < bodies.length; j++) pairs.push([0, j, 4]);
  for (let i = 1; i < bodies.length; i++) for (let j = i + 1; j < bodies.length; j++) pairs.push([i, j, 2.5]);
  for (const [ia, ib, lim] of pairs) {
    const a = bodies[ia], b = bodies[ib];
    let prev2 = Infinity, prev = Infinity;
    for (let k = 0; k < times.length; k++) {
      const sNow = A.AngleBetween(vecs[ia][k], vecs[ib][k]);
      if (k >= 2 && prev < prev2 && prev < sNow && prev < lim) {
        const tPrev = times[k - 1];
        const elong = A.AngleFromSun(b, new Date(tPrev));
        const view = elong > 12 ? bestViewing(a, b, tPrev, obs, lim + 1.5, ia === 0 ? 18 : 30) : null;
        if (view) {
          // From here, does the Moon actually cover the planet? Check the closest moment near the viewing time.
          let closest = { t: view.t, s: Infinity };
          if (a === A.Body.Moon) {
            for (let dt = -3 * 3600e3; dt <= 3 * 3600e3; dt += 5 * 60e3) {
              const d = new Date(view.t + dt);
              if (visibleAt(A.Body.Sun, d, obs) > -6 || visibleAt(b, d, obs) < 5) continue;
              const sv = sepFrom(a, b, d, obs);
              if (sv < closest.s) closest = { t: d.getTime(), s: sv };
            }
          }
          const moonR = 0.26;
          const id = `conj-${a}-${b}-${utcDay(view.t)}`;
          if (closest.s < moonR) {
            out.push({
              t: closest.t, kind: 'conjunction', icon: 'moon', weight: 3, title: `The Moon hides ${b}`,
              detail: `From here the Moon passes in front of ${b} (an occultation), around this time. Watch it vanish at the Moon’s edge; binoculars help.`,
              select: { kind: 'body', id: b }, id,
            });
          } else {
            const title = a === A.Body.Moon ? `The Moon passes ${b}` : `${a} and ${b} close together`;
            const apart = sepFrom(a, b, new Date(view.t), obs);
            out.push({
              t: view.t, kind: 'conjunction', icon: 'planet', weight: apart < 1.5 ? 3 : 2, title,
              detail: `About ${apart < 1 ? apart.toFixed(1) : Math.round(apart)}° apart, ${Math.round(view.alt)}° up in the ${compass(view.az)}: easy to spot by eye.`,
              select: { kind: 'body', id: b }, id,
            });
          }
        }
      }
      prev2 = prev;
      prev = sNow;
    }
  }

  // Equinoxes and solstices.
  for (const y of [from.getUTCFullYear(), from.getUTCFullYear() + 1]) {
    const s = A.Seasons(y);
    const list: [A.AstroTime, string][] = [[s.mar_equinox, 'March equinox'], [s.jun_solstice, 'June solstice'], [s.sep_equinox, 'September equinox'], [s.dec_solstice, 'December solstice']];
    for (const [t, name] of list) {
      const north = obs.latitude >= 0;
      const longestDay = name.startsWith('June') === north;
      if (inWin(t.date.getTime())) out.push({ t: t.date.getTime(), kind: 'season', icon: 'sun', weight: 1, title: name, detail: name.includes('equinox') ? 'Day and night are roughly equal across most of the world.' : longestDay ? 'The longest day of the year here.' : 'The longest night of the year here.', id: `season-${name.split(' ')[0].toLowerCase()}-${y}` });
    }
  }

  return out.filter((e) => inWin(e.t)).sort((a, b) => a.t - b.t);
}
